import Big from "big.js";
import { GQL, Model } from "@binale-tech/shared";
import { BuTaxesSKR } from "@domain/models/BuTaxUtils";
import { type BuTaxDB } from "@domain/models/Bu";
import { type BuTimeframe } from "@domain/context/BuContext";
import { SHConverter, type SHItem } from "@domain/core/SollHaben";
import { type PaymentsCtxData } from "@domain/context/accountingData/PaymentsProvider";
import type { GenericRecord } from "@domain/models";
import { defaultVatRowConfig } from "@accounting/common/overviews/UStVA/defaultDataSource";
import { type IVatRowData } from "@accounting/common/overviews/UStVA/types";
import {
    getVatAccountAmount,
    getVatAccountRoute,
    VAT_SONDERVORAUSZAHLUNG_ROUTE,
} from "@accounting/common/metrics/vatAccountRoutes";

export type VatPeriodRow = Pick<IVatRowData, "periods" | "titleSt" | "titleBmg">;

/** Kennzahl routes summed into X5 (output-side total) - mirrors VATProvider */
export const VAT_OUTPUT_SUM_ROUTES: readonly string[] = [
    "X1",
    "X2",
    "36",
    "80",
    "X3",
    "X4",
    "98",
    "96",
    "47",
    "74",
    "85",
];
/** Vorsteuer routes summed into X6 */
export const VAT_VST_SUM_ROUTES: readonly string[] = ["66", "61", "62", "67", "63", "59", "64"];
/** Routes summed into X8 (X7 is the computed X5−X6) */
export const VAT_X8_SUM_ROUTES: readonly string[] = ["X7", "65", "69"];

export type VatRowKind =
    | { kind: "direct" }
    | { kind: "rate"; rate: number }
    | { kind: "sum"; routes: readonly string[] }
    | { kind: "diff"; minuend: string; subtrahend: string };

/**
 * The only declaration of how derived Kennzahlen are composed. Consumed by
 * computeVatAggregatedSt, assembleVatRows and the reconciliation diff, so the
 * composition rules cannot drift between the table and its consumers.
 */
const VAT_ROW_KINDS: Readonly<Record<string, VatRowKind>> = {
    X1: { kind: "rate", rate: 0.19 },
    X2: { kind: "rate", rate: 0.07 },
    X3: { kind: "rate", rate: 0.19 },
    X4: { kind: "rate", rate: 0.07 },
    X5: { kind: "sum", routes: VAT_OUTPUT_SUM_ROUTES },
    X6: { kind: "sum", routes: VAT_VST_SUM_ROUTES },
    X7: { kind: "diff", minuend: "X5", subtrahend: "X6" },
    X8: { kind: "sum", routes: VAT_X8_SUM_ROUTES },
    "83": { kind: "diff", minuend: "X8", subtrahend: "39" },
};

export const getVatRowKind = (titleSt: string | undefined): VatRowKind =>
    (titleSt ? VAT_ROW_KINDS[titleSt] : undefined) ?? { kind: "direct" };

/** The Bemessungsgrundlage a rate row is derived from, e.g. X1 -> 81. */
export const getVatBmgRoute = (titleSt: string): string | undefined =>
    defaultVatRowConfig.find((row) => row.titleSt === titleSt)?.titleBmg;

/**
 * Tax on a Bemessungsgrundlage, rounded half away from zero (kaufmännisches Runden).
 * `Math.round` rounds half toward +Infinity and so is one cent wrong for every
 * negative BMG, i.e. any period where Gutschriften or Stornos exceed the Umsätze.
 */
export const getVatDerivedTax = (bmgCents: number, rate: number): number =>
    new Big(bmgCents).times(rate).round(0, Big.roundHalfUp).toNumber();

/**
 * Rebuilds derived filing routes from the transmitted real Kennzahlen.
 * Known limit: rate rows use today's rate table, so a mid-year rate change like
 * 2020 H2 would make the recomputed filed value incorrect for the affected period.
 */
export const assembleFiledRows = (kzValues: GQL.IElsterKzValue[]): Map<string, number> => {
    const filed = new Map(kzValues.map(({ kz, amount }) => [String(kz), amount]));
    const computed = new Map<string, number>();
    const resolve = (titleSt: string): number => {
        if (computed.has(titleSt)) {
            return computed.get(titleSt)!;
        }

        const kind = getVatRowKind(titleSt);
        let value: number;
        switch (kind.kind) {
            case "rate": {
                const titleBmg = getVatBmgRoute(titleSt);
                value = getVatDerivedTax(titleBmg ? (filed.get(titleBmg) ?? 0) : 0, kind.rate);
                break;
            }
            case "sum":
                value = kind.routes.reduce((total, route) => total + resolve(route), 0);
                break;
            case "diff":
                value = resolve(kind.minuend) - resolve(kind.subtrahend);
                break;
            default:
                value = filed.get(titleSt) ?? 0;
        }
        computed.set(titleSt, value);
        return value;
    };

    for (const titleSt of Object.keys(VAT_ROW_KINDS)) {
        filed.set(titleSt, resolve(titleSt));
    }
    return filed;
};

type VatRouting = { bmg?: number; ust?: number; vst?: number };

export type VatContributionKind = "bmg" | "st";

export type VatContribution = {
    recordKey: string;
    productKey: GQL.IProductKey;
    bu: number;
    isTax: boolean;
    /** Record-side account: Kreditor for ER, Debitor for Deb, Sachkonto elsewhere. */
    konto: string;
    /** Gegenkonto, the account of the leg that produced this contribution. */
    gegenkonto: string;
    isHaben: boolean;
    amount: number;
};

export type VatProvenance = Map<string, VatContribution[]>;

// Kennzahl routes are digits or X-prefixed, never containing ":" - safe as a composite key.
export const vatProvenanceKey = (period: number, route: string, kind: VatContributionKind): string =>
    `${period}:${route}:${kind}`;

// Rows add period 0 into January and 13 into December while keeping the source
// values, so provenance copies rather than moves.
const foldProvenancePeriod = (provenance: VatProvenance, from: number, to: number): void => {
    for (const [key, contributions] of [...provenance.entries()]) {
        const [periodStr, route, kind] = key.split(":");
        if (Number(periodStr) !== from) {
            continue;
        }
        const targetKey = vatProvenanceKey(to, route, kind as VatContributionKind);
        const target = provenance.get(targetKey);
        if (target) {
            target.push(...contributions);
        } else {
            provenance.set(targetKey, [...contributions]);
        }
    }
};

const buModeV: number[] = [
    6,
    7,
    Model.Bu.BU8,
    Model.Bu.BU9,
    16,
    17,
    18,
    19,
    Model.Bu.BU91,
    Model.Bu.BU92,
    Model.Bu.BU94,
    Model.Bu.BU95,
    96,
    97,
    98,
    99,
];

/**
 * Stage-1 UStVA aggregation, extracted verbatim from VATProvider's periodsDraftData
 * memo: walks the year's records through SHConverter in VAT mode and distributes
 * BMG/St amounts onto the Kennzahl routes of defaultVatRowConfig, folding period 0
 * into January and period 13 into December.
 */
export const computeVatPeriodRowsWithProvenance = (
    yearConfig: GQL.ICompanyAccountingYear,
    companyBuTimeframes: BuTimeframe[],
    yearRecords: GenericRecord[],
    payments: PaymentsCtxData,
): { rows: VatPeriodRow[]; provenance: VatProvenance } => {
    const provenance: VatProvenance = new Map();
    if (!yearConfig || yearRecords.length === 0) {
        return { rows: [], provenance };
    }
    if (!companyBuTimeframes) {
        return { rows: [], provenance };
    }
    if (yearConfig.year < 2022) {
        return { rows: [], provenance };
    }
    const converter = new SHConverter(yearConfig, companyBuTimeframes, payments);
    const buTimeframe = BuTaxesSKR.getBuTimeframeYearPeriod(yearConfig.skr, companyBuTimeframes, yearConfig.year, 1);
    const getVatRoutes = (v: BuTaxDB, ust13b: Model.USt13bOption): VatRouting => {
        if (ust13b) {
            const ust13bVatRouting = Model.getUst13bData(v.bu).options?.find(
                (o) => String(o.value) === String(ust13b),
            )?.vatRouting;
            if (ust13bVatRouting) {
                return ust13bVatRouting;
            }
        }

        const vatRouting: VatRouting = {};
        if (Number.isFinite(v.MBMG)) {
            vatRouting.bmg = v.MBMG as number;
        }
        if (Number.isFinite(v.MSteuer)) {
            vatRouting.ust = v.MSteuer as number;
        }
        if (Number.isFinite(v.VSteuer)) {
            vatRouting.vst = v.VSteuer;
        }
        return vatRouting;
    };
    const getBuAmount = (shItem: SHItem, routeType: "ust" | "vst" | "bmg"): number => {
        if (buModeV.includes(shItem.bu)) {
            // bmg S+ / H-
            // vst S+ / H-
            // ust S- / H+
            switch (routeType) {
                case "bmg":
                case "vst":
                    return shItem.isHaben ? -shItem.amount : shItem.amount;
                case "ust":
                    return shItem.isHaben ? shItem.amount : -shItem.amount;
            }
        }
        // bmg S- / H+
        // ust S- / H+
        return shItem.isHaben ? shItem.amount : -shItem.amount;
    };
    const addToSegment = (
        vatRoute: number | string,
        period: number,
        distribution: Map<string, number>[],
        shItem: SHItem,
        kind: VatContributionKind,
        record: GenericRecord,
        amount: number,
    ) => {
        if (!vatRoute) {
            return;
        }
        const vatRouteStr = String(vatRoute);
        const periodSegment = distribution[period];
        if (!periodSegment.has(vatRouteStr)) {
            periodSegment.set(vatRouteStr, 0);
        }

        // one value feeds both the sum and the provenance, so they cannot drift apart
        periodSegment.set(vatRouteStr, periodSegment.get(vatRouteStr) + amount);

        const key = vatProvenanceKey(period, vatRouteStr, kind);
        const contribution: VatContribution = {
            recordKey: record.key,
            productKey: record.getProductKey(),
            bu: shItem.bu ?? 0,
            isTax: Boolean(shItem.isTax),
            konto: record.getRecordCategoryCreditor()?.getExtNumPrint(yearConfig.kontoExt) ?? "",
            // captured here because the SHItem is in scope; deriving it from the record
            // later is ambiguous for a multi-item record
            gegenkonto: shItem.konto?.getExtNumPrint(yearConfig.kontoExt) ?? "",
            isHaben: Boolean(shItem.isHaben),
            amount,
        };
        const list = provenance.get(key);
        if (list) {
            list.push(contribution);
        } else {
            provenance.set(key, [contribution]);
        }
    };
    const periodsDistributionBmg: Map<string, number>[] = new Array(14).fill(undefined).map(() => new Map());
    const periodsDistributionSt: Map<string, number>[] = new Array(14).fill(undefined).map(() => new Map());
    const hasSondervorauszahlung =
        yearConfig.ustvaFrequency === GQL.IUstvaFilingFrequency.Monthly && yearConfig.dauerfristverlaengerung;
    yearRecords.forEach((v) => {
        converter.getItemsVatMode(v).forEach((item) => {
            // a line on a mapped tax account is routed by its account only, never also by its BU
            const accountRoute = getVatAccountRoute(yearConfig.skr, item.konto);
            if (accountRoute) {
                const amount = getVatAccountAmount(accountRoute, Boolean(item.isHaben), item.amount);
                if (accountRoute !== VAT_SONDERVORAUSZAHLUNG_ROUTE) {
                    addToSegment(accountRoute, v.period, periodsDistributionSt, item, "st", v, amount);
                } else if (hasSondervorauszahlung) {
                    addToSegment(accountRoute, 12, periodsDistributionSt, item, "st", v, amount);
                }
                return;
            }
            if (Number.isFinite(item.bu) && item.bu > 0) {
                const buData = buTimeframe.items.find((buTax) => buTax.bu === item.bu);
                if (!buData) {
                    return;
                }
                const { bmg, ust, vst } = getVatRoutes(buData, item.USt13b);
                if (item.isTax) {
                    if (item.taxType === "ust") {
                        addToSegment(ust, v.period, periodsDistributionSt, item, "st", v, getBuAmount(item, "ust"));
                    }
                    if (item.taxType === "vst") {
                        addToSegment(vst, v.period, periodsDistributionSt, item, "st", v, getBuAmount(item, "vst"));
                    }
                } else {
                    addToSegment(bmg, v.period, periodsDistributionBmg, item, "bmg", v, getBuAmount(item, "bmg"));
                }
            }
        });
    });
    const periodRows = defaultVatRowConfig.map((data, _index) => {
        const periods = new Array(14).fill(undefined).map((_, pidx) => {
            const bmgDraft = periodsDistributionBmg[pidx].get(data.titleBmg) ?? 0;
            // bmg should be rounded to the euro (no cents) // or not
            const bmg = bmgDraft; //Math.floor(bmgDraft / 100) * 100;
            const getSt = () => {
                if (!data.titleSt) {
                    return 0;
                }
                if (data.titleSt.startsWith("X")) {
                    if (["X1", "X3"].includes(data.titleSt)) {
                        return getVatDerivedTax(bmg, 0.19);
                    }
                    if (["X2", "X4"].includes(data.titleSt)) {
                        return getVatDerivedTax(bmg, 0.07);
                    }
                }
                return periodsDistributionSt[pidx].get(data.titleSt) ?? 0;
            };
            return {
                bmg,
                st: getSt(),
            };
        });
        // add period 01.01 to January
        periods[1].st += periods[0].st;
        periods[1].bmg += periods[0].bmg;
        // add period 31.12 to December
        periods[12].st += periods[13].st;
        periods[12].bmg += periods[13].bmg;
        const periodRow: VatPeriodRow = {
            periods,
            titleSt: data.titleSt,
            titleBmg: data.titleBmg,
        };
        return periodRow;
    });

    foldProvenancePeriod(provenance, 0, 1);
    foldProvenancePeriod(provenance, 13, 12);

    return { rows: periodRows, provenance };
};

/**
 * Rows-only view of {@link computeVatPeriodRowsWithProvenance}. Same computation,
 * unchanged signature, so existing callers that do not need provenance are untouched.
 */
export const computeVatPeriodRows = (
    yearConfig: GQL.ICompanyAccountingYear,
    companyBuTimeframes: BuTimeframe[],
    yearRecords: GenericRecord[],
    payments: PaymentsCtxData,
): VatPeriodRow[] => computeVatPeriodRowsWithProvenance(yearConfig, companyBuTimeframes, yearRecords, payments).rows;

/**
 * Derived UStVA aggregate rows, pure. Mirrors VATProvider's in-place post-processing
 * (X5/X6/X7/X8 and Kennzahl 83 = Zahllast/Erstattung) without touching the inputs.
 */
export const computeVatAggregatedSt = (rows: VatPeriodRow[]): Map<string, number[]> => {
    const zeros = () => new Array<number>(14).fill(0);
    const stOf = (titleSt: string): number[] => {
        const row = rows.find((r) => r.titleSt === titleSt);
        return zeros().map((_, pidx) => row?.periods[pidx]?.st ?? 0);
    };
    // Resolve through VAT_ROW_KINDS so a derived route reads its computed value rather
    // than the stage-1 zero stored on its row (X7/X8 carry st=0 at that point), while
    // direct and rate rows keep the st they already got in stage 1.
    const computed = new Map<string, number[]>();
    const resolve = (titleSt: string): number[] => {
        const cached = computed.get(titleSt);
        if (cached) {
            return cached;
        }
        const kind = getVatRowKind(titleSt);
        let value: number[];
        switch (kind.kind) {
            case "sum":
                value = kind.routes.map(resolve).reduce((acc, arr) => acc.map((v, i) => v + arr[i]), zeros());
                break;
            case "diff": {
                const subtrahend = resolve(kind.subtrahend);
                value = resolve(kind.minuend).map((v, i) => v - subtrahend[i]);
                break;
            }
            default:
                value = stOf(titleSt);
        }
        computed.set(titleSt, value);
        return value;
    };
    return new Map(VAT_AGGREGATE_ROUTES.map((titleSt) => [titleSt, resolve(titleSt)]));
};

/** Kennzahlen whose St is derived rather than summed from records. */
const VAT_AGGREGATE_ROUTES: readonly string[] = ["X5", "X6", "X7", "X8", "83"];

/**
 * Builds the full display rows: stage-1 per-period values, the derived X5/X6/X7/X8/83
 * St values, and the year and quarter aggregates. Pure - never mutates `rows`.
 */
export const assembleVatRows = (rows: VatPeriodRow[]): IVatRowData[] => {
    if (!rows.length) {
        return [];
    }
    const aggregated = computeVatAggregatedSt(rows);
    return defaultVatRowConfig.map((data, rowIdx) => {
        const source = rows[rowIdx];
        const override = data.titleSt ? aggregated.get(data.titleSt) : undefined;
        const periods = source.periods.map((segment, pidx) => ({
            bmg: segment.bmg,
            st: override ? override[pidx] : segment.st,
        }));
        const sumOver = (predicate: (idx: number) => boolean) => ({
            bmg: periods.filter((_, idx) => predicate(idx)).reduce((acc, v) => acc + v.bmg, 0),
            st: periods.filter((_, idx) => predicate(idx)).reduce((acc, v) => acc + v.st, 0),
        });
        return {
            ...data,
            key: `${rowIdx}:${data.titleSt}:${data.titleBmg}`,
            periods,
            // periods 0 and 13 were already folded into 1 and 12, so including them would double-count
            year: sumOver((idx) => idx > 0 && idx < 13),
            quartals: Array(4)
                .fill(undefined)
                .map((_, qidx) => sumOver((pidx) => pidx > qidx * 3 && pidx <= (qidx + 1) * 3)),
        } satisfies IVatRowData;
    });
};
